import styled from "@emotion/styled";
import { Checkbox, Menu } from "antd";
import axios, { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";
import { Toast } from "react-bootstrap";
import { FiAlertCircle, FiX } from "react-icons/fi";
import { SecureConnection } from "../../components/bankFeeds/secureConnection";
import { getBackendUrl, isDev, STORES } from "../../utils";
import { BackNextButton } from "./BackNextButton";
import {
  ATTEMPTED_LINKING_DATA_KEY,
  BankFeedAccountLinkingData,
  CONFIRMED_LINKING_DATA_KEY,
  Currency,
  GLAccountProps,
  INITIAL_ACCOUNT_LENGTH,
  INVALID_STATE_ERROR,
  PENDING_LINKING_DATA_KEY,
  Subsidiary,
} from "./constants";
import { CreateGLAccountModal } from "./CreateGLAccount/CreateGLAccountModal";
import { DropdownLinked } from "./Dropdown/DropdownLinked";
import { DropdownUnlinked } from "./Dropdown/DropdownUnlinked";
import { GenericBankFeedsAccountName } from "./GenericBankFeedsAccountName";
import { GenericBankFeedsButton } from "./GenericBankFeedsButton";
import ScrollableComponent from "./ScrollableComponent";
import { trackAmplitudeEvent } from "./utils/shared";
import {
  fetchAllEntitiesWithPagination,
  generateGetAccountLinkingValues,
} from "./utils/stepOne";

const DROPDOWN_PAGE_SIZE = 20;

interface GLAccount {
  id: string;
  name: string;
  currency_code: string;
  account_type: string;
}

interface BankFeedAccount {
  id: string;
  name: string;
  account_id?: string;
  current_balance: number | null;
  bank_account_type: string;
}

const getGLAccountDisplayName = (glAccount: GLAccount): string => {
  return `${glAccount.name} - ${glAccount.currency_code}`;
};

const generateLinkingData = (
  glAccount: GLAccount,
  bfAccount: BankFeedAccount,
  isChecked: boolean = true,
): BankFeedAccountLinkingData => {
  return {
    bfAccountId: bfAccount.id,
    glAccountId: glAccount.id,
    bfAccountName: bfAccount.name,
    glAccountName: getGLAccountDisplayName(glAccount),
    isChecked,
  };
};

const isGLAccountSelectable = (
  glAccount: GLAccount,
  bfAccount: BankFeedAccount,
  invalidAccountIds: Set<string>,
) => {
  return (
    !invalidAccountIds.has(glAccount.id) &&
    glAccount.account_type === bfAccount.bank_account_type
  );
};

export const GenericBankFeedsLinkStepOneComponent = ({
  token,
  store,
  setStep,
  setStore,
  prelinked,
  platformName,
  setPlatformName,
  setErrorMessage,
  setRetryCallback,
  setRetryableErrorMessage,
  setConfirmedBankFeedAccountLinkingData,
}: {
  token: string;
  store: STORES | null;
  setStep: (value: number) => void;
  setStore: (value: STORES) => void;
  prelinked: string;
  platformName: string | null;
  setPlatformName: (value: string) => void;
  setErrorMessage: (value: string) => void;
  setRetryCallback: (fn: () => void) => void;
  setRetryableErrorMessage: (value: string) => void;
  setConfirmedBankFeedAccountLinkingData: (
    value: BankFeedAccountLinkingData[],
  ) => void;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [itemId, setItemId] = useState<string | null>(null);

  // Number of total succeeded / attempted account links from a previous run.
  const [totalSucceededAccounts, setTotalSucceededAccounts] = useState(0);
  const [totalAttemptedAccounts, setTotalAttemptedAccounts] = useState(0);

  const [hoverId, setHoverId] = useState<string | null>(null);
  const [linkedAccountIds, setLinkedAccountIds] = useState(new Set<string>());
  const [unlinkedAccounts, setUnlinkedAccounts] = useState([] as GLAccount[]);

  // All linked and unlinked bank feed accounts
  const [linkedBankFeedAccounts, setLinkedBankFeedAccounts] = useState(
    [] as BankFeedAccount[],
  );
  const [unlinkedBankFeedAccounts, setUnlinkedBankFeedAccounts] = useState(
    [] as BankFeedAccount[],
  );
  const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>(
    [],
  );
  const [availableSubsidiaries, setAvailableSubsidiaries] = useState<
    Subsidiary[]
  >([]);

  // The bank feed account whose dropdown menu is currently open.
  const [visibleBfAccount, setVisibleBfAccount] =
    useState<BankFeedAccount | null>(null);
  // The bank feed account which the mouse is currently hovering over.
  const [highlightedBfAccount, setHighlightedBfAccount] =
    useState<BankFeedAccount | null>(null);

  // Current linking data for each bank feed account, including GL account ID and whether it is currently checked.
  const [accountLinkingData, setAccountLinkingData] = useState(
    {} as Record<string, BankFeedAccountLinkingData>,
  );
  // Accounts that were linked prior to starting this workflow.
  const [previouslyLinkedData, setPreviouslyLinkedData] = useState(
    {} as Record<string, BankFeedAccountLinkingData>,
  );

  // GL account IDs which are set to be linked to bank feed accounts. Use to filter already selected GL accounts from the dropdown.
  const [selectedGLAccountIds, setSelectedGLAccountIds] = useState(
    new Set<string>(),
  );

  const [linkAccountText, setLinkAccountText] = useState("Link Account");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isToastVisible, setIsToastVisible] = useState(false);

  const updateItemDetails = (apiResponse: AxiosResponse<any>) => {
    const responseStore = apiResponse.data?.connection?.platform;
    const responseItemId = apiResponse.data?.connection?.id;

    if (!store && responseStore) {
      setStore(responseStore);
      setPlatformName(getDisplayPlatformName(responseStore));
    }

    if (!itemId && responseItemId) {
      setItemId(responseItemId);
    }
  };

  const getStateMetadata = () => {
    return {
      token,
      updateItemDetails,
      updateErrorMessage,
      trackStepOneAmplitudeEvent,
    };
  };

  const trackStepOneAmplitudeEvent = (
    event: string,
    props: Record<string, any> = {},
  ) => {
    const fullProps = {
      step: 1,
      itemId,
      platform: store,
      ...props,
    };

    trackAmplitudeEvent(event, fullProps);
  };

  const getAccountLinkingValues = generateGetAccountLinkingValues(
    trackStepOneAmplitudeEvent,
  );

  const setDefaultRetryCallback = () => {
    setRetryCallback(() => {
      return () => {
        window.location.reload();
      };
    });
  };

  const saveLinkedState = () => {
    localStorage.setItem(
      PENDING_LINKING_DATA_KEY,
      JSON.stringify(accountLinkingData),
    );
  };

  const getDisplayPlatformName = (platform: string): string => {
    switch (platform) {
      case STORES.NETSUITE:
        return "NetSuite";
      case STORES.XERO:
        return "Xero";
      default:
        updateErrorMessage({
          message: `Platform ${platform} is not supported.`,
        });
        return "";
    }
  };

  const updateErrorMessage = (args: {
    error?: any;
    message?: string;
    retryable?: boolean;
  }) => {
    const { error, message, retryable } = args;
    saveLinkedState();

    if (message) {
      trackStepOneAmplitudeEvent("Encountered an error", {
        errorMessage: message,
      });
      // Assume messages are NOT retryable unless otherwise specified.
      if (retryable) {
        setRetryableErrorMessage(message);
      } else {
        setErrorMessage(message);
      }
    } else if (!error) {
      trackStepOneAmplitudeEvent("Encountered an unknown error");
      setRetryableErrorMessage("Unknown error occurred. Please try again!");
    } else {
      const responseData = error.response?.data;
      if (responseData?.error_code === "ENDPOINT_NOT_AVAILABLE_FOR_PLATFORM") {
        setErrorMessage(`Platform is not supported.`);
        return;
      }

      const errorMessage = responseData?.error_message ?? error.message;
      trackStepOneAmplitudeEvent("Encountered an error", { errorMessage });
      setRetryableErrorMessage(errorMessage);
    }
  };

  useEffect(() => {
    // This code runs once on first render
    setDefaultRetryCallback();
  }, []);

  // Load all available currencies.
  useEffect(() => {
    if (availableCurrencies.length > 0) {
      // Only fetch currencies once on page load.
      return;
    }

    const handleFetchedCurrencies = (entities: any[]) => {
      const currencies = entities as Currency[];

      setAvailableCurrencies(
        currencies.sort((a, b) => (a.currency_code > b.currency_code ? 1 : -1)),
      );
    };

    fetchAllEntitiesWithPagination(
      { endpoint: "currencies", key: "currencies", description: "currencies" },
      getStateMetadata(),
      handleFetchedCurrencies,
    );
  }, [setAvailableCurrencies]);

  // Load all available subsidiaries (if applicable).
  useEffect(() => {
    if (
      availableSubsidiaries.length > 0 ||
      store === null ||
      store !== STORES.NETSUITE
    ) {
      // Only fetch subsidiaries once on page load IF platform is Netsuite.
      return;
    }

    const handleFetchedSubsidiaries = (entities: any[]) => {
      const subsidiaries = entities as Subsidiary[];

      setAvailableSubsidiaries(
        subsidiaries
          .filter((x) => x.status === "active")
          .sort((a, b) => (a.name > b.name ? 1 : -1)),
      );
    };

    fetchAllEntitiesWithPagination(
      {
        endpoint: "subsidiaries",
        key: "subsidiaries",
        description: "subsidiaries",
      },
      getStateMetadata(),
      handleFetchedSubsidiaries,
    );
  }, [platformName, setAvailableSubsidiaries]);

  useEffect(() => {
    const handleFetchedBankFeedAccounts = (entities: any[]) => {
      const bankFeedAccounts = entities as BankFeedAccount[];

      const accountIds = new Set<string>();
      const linkedBFAccounts: BankFeedAccount[] = [];
      const unlinkedBFAccounts: BankFeedAccount[] = [];

      // If we have attempted account values (from step 3 of a previous run), we need to only display those accounts.
      const attemptedAccountLinkingValues = getAccountLinkingValues(
        ATTEMPTED_LINKING_DATA_KEY,
      );
      const attemptedAccountIds = new Set(
        attemptedAccountLinkingValues.map((value) => value.bfAccountId),
      );

      for (const account of bankFeedAccounts) {
        if (account.account_id) {
          // Need to keep track of all linked account IDs regardless of whether we display the bank feed accounts.
          accountIds.add(account.account_id);
        }

        // If we have started over from attempt where *some* link attempts fail, we should only show the
        // bank feed accounts involved in that attempt.
        if (
          attemptedAccountIds.size > 0 &&
          !attemptedAccountIds.has(account.id)
        ) {
          continue;
        }

        if (account.account_id) {
          linkedBFAccounts.push(account);
        } else {
          unlinkedBFAccounts.push(account);
        }
      }

      if (attemptedAccountIds.size > 0) {
        if (unlinkedBFAccounts.length === 0) {
          // If we reloaded from an attempted error and there are no unlinked accounts, we got out of sync somehow - try again from scratch!
          localStorage.removeItem(ATTEMPTED_LINKING_DATA_KEY);
          updateErrorMessage(INVALID_STATE_ERROR);
          return;
        }
        setTotalSucceededAccounts(linkedBFAccounts.length);
        setTotalAttemptedAccounts(attemptedAccountIds.size);
      }

      setLinkedAccountIds(accountIds);
      setLinkedBankFeedAccounts(linkedBFAccounts);
      setUnlinkedBankFeedAccounts(unlinkedBFAccounts);

      if (unlinkedBFAccounts.length === 0) {
        updateErrorMessage({
          message:
            "No unlinked bank feed accounts found! Please try reloading the page. If the problem persists, contact your bank.",
          retryable: true,
        });
      }
    };

    // Need to fetch ALL bank feed accounts, so we can check which GL accounts are already linked.
    fetchAllEntitiesWithPagination(
      {
        endpoint: "bank_feeds/accounts",
        key: "bank_feed_accounts",
        description: "bank feed accounts",
      },
      getStateMetadata(),
      handleFetchedBankFeedAccounts,
    );
  }, [
    token,
    setIsLoading,
    setLinkedAccountIds,
    setLinkedBankFeedAccounts,
    setUnlinkedBankFeedAccounts,
  ]);

  useEffect(() => {
    // Skip fetching G/L accounts until we know there are unlinked bank feed accounts.
    if (unlinkedBankFeedAccounts.length === 0 || unlinkedAccounts.length > 0) {
      return;
    }

    const handleFetchedAccounts = (entities: any[]) => {
      const accounts = entities as GLAccount[];

      const fetchedUnlinkedAccounts: GLAccount[] = [];
      const linkedData: Record<string, BankFeedAccountLinkingData> = {};

      for (const account of accounts) {
        if (linkedAccountIds.has(account.id)) {
          // Populate data for accounts that were linked previously, if any.
          const bfAccount = linkedBankFeedAccounts.find(
            (acc) => acc.account_id === account.id,
          );
          if (bfAccount) {
            linkedData[bfAccount.id] = {
              bfAccountId: bfAccount.id,
              bfAccountName: bfAccount.name,
              glAccountId: account.id,
              glAccountName: getGLAccountDisplayName(account),
              isChecked: true,
            };
          }
        } else {
          fetchedUnlinkedAccounts.push(account);
        }
      }

      setPreviouslyLinkedData(linkedData);
      setUnlinkedAccounts(fetchedUnlinkedAccounts);
    };

    fetchAllEntitiesWithPagination(
      { endpoint: "accounts", key: "accounts", description: "G/L accounts" },
      getStateMetadata(),
      handleFetchedAccounts,
    );
  }, [
    linkedAccountIds,
    unlinkedBankFeedAccounts,
    setIsLoading,
    setUnlinkedAccounts,
    setPreviouslyLinkedData,
  ]);

  useEffect(() => {
    if (
      unlinkedAccounts.length === 0 ||
      unlinkedBankFeedAccounts.length === 0 ||
      Object.keys(accountLinkingData).length > 0
    ) {
      // Skip if data has not been fully loaded yet. If data HAS been fully loaded but one of these
      // are still empty, it's okay to skip since there's nothing we can link anyway.
      // Also skip prelinking step if we already have recorded linked data.
      return;
    }

    const prelinkedData: Record<string, BankFeedAccountLinkingData> =
      accountLinkingData;
    const preselectedAccountIds = new Set<string>();

    // Check if we have linked data saved from a previous flow that errored out (either from this step or steps 2/3).
    const attemptedAccountLinkingValues = getAccountLinkingValues(
      ATTEMPTED_LINKING_DATA_KEY,
    );
    const confirmedAccountLinkingValues = getAccountLinkingValues(
      CONFIRMED_LINKING_DATA_KEY,
    );
    const pendingAccountLinkingValues = getAccountLinkingValues(
      PENDING_LINKING_DATA_KEY,
    );

    // Choose whichever set of linking data actually has data, but prefer the attempted values, then confirmed values (as these are from later on in the flow).
    const savedAccountLinkingValues =
      attemptedAccountLinkingValues.length > 0
        ? attemptedAccountLinkingValues
        : confirmedAccountLinkingValues.length > 0
        ? confirmedAccountLinkingValues
        : pendingAccountLinkingValues;

    if (savedAccountLinkingValues.length > 0) {
      trackStepOneAmplitudeEvent(
        "Restarting with saved account linking data.",
        {
          savedAccountLinkingValues,
        },
      );

      for (const data of savedAccountLinkingValues) {
        const { glAccount, bfAccount } = getGLAndBFAccountsByID(
          data.glAccountId,
          data.bfAccountId,
        );
        if (!glAccount || !bfAccount) {
          // If either account is missing, just skip to the next one.
          // This may happen if we are starting from the last step, and some accounts were successfully linked.
          continue;
        }

        // Make sure everything we saved previously is kosher, then re-add to accountLinkingData
        if (
          isGLAccountSelectable(glAccount, bfAccount, preselectedAccountIds)
        ) {
          preselectedAccountIds.add(glAccount.id);
          // Re-generate the linking data in case anything changed.
          prelinkedData[bfAccount.id] = generateLinkingData(
            glAccount,
            bfAccount,
            data.isChecked,
          );
        }
      }
    } else if (isDev() && prelinked && unlinkedAccounts.length > 0) {
      // For testing only - prelink accounts so we can get through this screen faster!
      // Note that this will load forever if there are no unlinked G/L accounts. Since it's just for dev it doesn't
      // matter, just don't include prelinked in that case.
      const prelinkedCount = parseInt(prelinked);
      // Start off with current accountLinkingData so more accounts aren't linked on every hot reload.

      for (const bfAccount of unlinkedBankFeedAccounts) {
        if (Object.keys(prelinkedData).length >= prelinkedCount) {
          break;
        }

        const selectableGLAccounts = getSelectableGLAccounts(
          bfAccount,
          preselectedAccountIds,
        );
        if (selectableGLAccounts.length === 0) {
          continue;
        }

        const glAccount = selectableGLAccounts[0];
        preselectedAccountIds.add(glAccount.id);

        prelinkedData[bfAccount.id] = generateLinkingData(glAccount, bfAccount);
      }
    }

    trackStepOneAmplitudeEvent("Successfully preloaded account linking data", {
      accountLinkingData: prelinkedData,
    });

    setSelectedGLAccountIds(preselectedAccountIds);
    setAccountLinkingData(prelinkedData);
    setIsLoading(false);

    localStorage.removeItem(PENDING_LINKING_DATA_KEY);
    localStorage.removeItem(CONFIRMED_LINKING_DATA_KEY);
    localStorage.removeItem(ATTEMPTED_LINKING_DATA_KEY);
  }, [
    unlinkedAccounts,
    setIsLoading,
    setAccountLinkingData,
    setSelectedGLAccountIds,
  ]);

  useEffect(() => {
    // Display toast if there was an error and the page is fully loaded (to make sure toaster is mounted).
    if (!isLoading && totalAttemptedAccounts > 0) {
      trackStepOneAmplitudeEvent("Error linking one or more accounts", {
        totalAttemptedAccounts,
        totalSucceededAccounts,
        accountLinkingData,
      });
      setIsToastVisible(true);
    }
  }, [isLoading, totalSucceededAccounts, totalAttemptedAccounts]);

  // Update button text based on how many linked accounts are active.
  useEffect(() => {
    const checkedLinkedAccounts = getCheckedAccountLinkingData();
    if (checkedLinkedAccounts.length > 1) {
      setLinkAccountText("Link Accounts");
    } else {
      setLinkAccountText("Link Account");
    }
  }, [accountLinkingData]);

  const linkGLAndBFAccounts = (
    glAccount?: GLAccount,
    bfAccount?: BankFeedAccount,
  ) => {
    if (!glAccount || !bfAccount) {
      trackStepOneAmplitudeEvent("One or more linked accounts are missing", {
        glAccount,
        bfAccount,
      });
      updateErrorMessage(INVALID_STATE_ERROR);
      return;
    }

    const oldGlAccountId = accountLinkingData[bfAccount.id]?.glAccountId;
    setSelectedGLAccountIds(selectedGLAccountIds.add(glAccount.id));

    if (oldGlAccountId) {
      selectedGLAccountIds.delete(oldGlAccountId);
      setSelectedGLAccountIds(selectedGLAccountIds);
    }

    setAccountLinkingData({
      ...accountLinkingData,
      [bfAccount.id]: generateLinkingData(glAccount, bfAccount),
    });

    // Once this bank feed account is linked, the dropdown will always be visible. Free up the visible
    // bank feed account so it can be set to something else.
    setVisibleBfAccount(null);
  };

  const getGLAndBFAccountsByID = (
    glAccountId: string,
    bfAccountId: string,
  ): { glAccount?: GLAccount; bfAccount?: BankFeedAccount } => {
    return {
      glAccount: unlinkedAccounts.find((acc) => acc.id === glAccountId),
      bfAccount: unlinkedBankFeedAccounts.find((acc) => acc.id === bfAccountId),
    };
  };

  const selectGLAccount = (e: any) => {
    if (e.key === "add-new-account") {
      // This will open the modal to create a new GL account for the currently visible bank feed account.
      trackStepOneAmplitudeEvent("Adding a new G/L account.");
      setIsModalVisible(true);
      return;
    }

    const parts = e.key.split("::");
    const glAccountId = parts[1];
    const bfAccountId = parts[0];

    trackStepOneAmplitudeEvent("Selected G/L account.", {
      glAccountId,
      bfAccountId,
    });

    // Error if this G/L account has already been linked to another BF account.
    if (selectedGLAccountIds.has(glAccountId)) {
      trackStepOneAmplitudeEvent("G/L account is already linked", {
        glAccountId,
        bfAccountId,
      });
      updateErrorMessage(INVALID_STATE_ERROR);
      return;
    }

    const { glAccount, bfAccount } = getGLAndBFAccountsByID(
      glAccountId,
      bfAccountId,
    );
    linkGLAndBFAccounts(glAccount, bfAccount);
  };

  const getCheckedAccountLinkingData = (): BankFeedAccountLinkingData[] => {
    return Object.entries(accountLinkingData)
      .filter(([_, data]) => data.isChecked)
      .map(([_, data]) => data);
  };

  const getSelectableGLAccounts = (
    bfAccount: BankFeedAccount,
    overrideAccountIds?: Set<string>,
  ) => {
    if (!!bfAccount.account_id) {
      // If bank feed account is already linked, cannot link a new G/L account.
      return [];
    }

    // It is currently possible that no selectable accounts will be available to link to a given bank feed account.
    // However, after DEV-19064 there will always be the option to create a new G/L account.
    return unlinkedAccounts.filter((glAccount) =>
      isGLAccountSelectable(
        glAccount,
        bfAccount,
        overrideAccountIds ?? selectedGLAccountIds,
      ),
    );
  };

  const getSelectableGLAccountDropdownOptions = useCallback(
    (
      bfAccount: BankFeedAccount,
      page: number,
      setPage: (page: number) => void,
    ) => {
      const selectableGLAccounts = getSelectableGLAccounts(bfAccount);
      const start = page * DROPDOWN_PAGE_SIZE;
      const visibleGLAccounts = selectableGLAccounts.slice(
        start,
        start + DROPDOWN_PAGE_SIZE,
      );

      return (
        <Menu
          onClick={selectGLAccount}
          style={{
            width: 200,
            height: 200,
            overflowY: "auto",
            overflowX: "hidden",
          }}>
          <Menu.Item key="add-new-account">{"<Add new account>"}</Menu.Item>
          {visibleGLAccounts.map((acc) => (
            <Menu.Item
              key={`${bfAccount.id}::${acc.id}`}
              style={{ overflow: "visible", position: "relative" }}>
              <GenericBankFeedsAccountName
                id={`${bfAccount.id}_${acc.id}`}
                text={getGLAccountDisplayName(acc)}
              />
            </Menu.Item>
          ))}
          <Menu.Item disabled>
            <div
              style={{
                width: "100%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
              <BackNextButton
                text="<< Last"
                onClick={() => setPage(page - 1)}
                disabled={page === 0}></BackNextButton>
              <BackNextButton
                text="Next >>"
                onClick={() => setPage(page + 1)}
                disabled={
                  (page + 1) * DROPDOWN_PAGE_SIZE >= selectableGLAccounts.length
                }></BackNextButton>
            </div>
          </Menu.Item>
        </Menu>
      );
    },
    // getSelectableGLAccountDropdownOptions must depend on accountLinkingData, or we may not be operating on the most recent linking data.
    // Because of this, all unlinked bank account components will be reloaded on any changes to accountLinkingData (account selection, checkbox toggling).
    // This seems pretty instant for even a large number of bank accounts & G/L accounts (in the hundreds), but we can revisit this if it beings to
    // noticeable slow down the linking process.
    [unlinkedAccounts, selectedGLAccountIds, accountLinkingData],
  );

  const shouldShowDropdown = (bfAccountId: string) => {
    // If a G/L account has already been selected, always show the dropdown.
    if (accountLinkingData[bfAccountId]) {
      return true;
    }

    // If a dropdown is currently open, don't hide and it and don't show any other account dropdowns.
    if (visibleBfAccount) {
      return visibleBfAccount.id === bfAccountId;
    }

    // Only one bank feed account should be highlighted at a time.
    return highlightedBfAccount?.id === bfAccountId;
  };

  const getGLAccountDropdownLabel = (
    bfAccount: BankFeedAccount,
    linked: boolean,
  ) => {
    const linkingData = linked ? previouslyLinkedData : accountLinkingData;
    const defaultText = linked ? "Unknown" : "Select general ledger";

    return linkingData[bfAccount.id]
      ? linkingData[bfAccount.id].glAccountName
      : defaultText;
  };

  const getDropdownComponent = useCallback(
    (linked: boolean, bfAccount: BankFeedAccount) => {
      if (linked) {
        return (
          <DropdownLinked
            bfAccount={bfAccount}
            glAccountDropdownLabel={getGLAccountDropdownLabel(
              bfAccount,
              linked,
            )}></DropdownLinked>
        );
      }

      return (
        <DropdownUnlinked
          bfAccount={bfAccount}
          setHoverId={setHoverId}
          shouldShowHover={hoverId === bfAccount.id}
          shouldShowDropdown={shouldShowDropdown(bfAccount.id)}
          setVisibleBfAccount={setVisibleBfAccount}
          glAccountDropdownLabel={getGLAccountDropdownLabel(bfAccount, linked)}
          getSelectableGLAccountDropdownOptions={
            getSelectableGLAccountDropdownOptions
          }></DropdownUnlinked>
      );
    },
    [
      hoverId,
      setHoverId,
      shouldShowDropdown,
      setVisibleBfAccount,
      getGLAccountDropdownLabel,
      getSelectableGLAccountDropdownOptions,
    ],
  );

  const getFormattedBankFeedAccounts = (
    bankFeedAccounts: any[],
    linked: boolean,
  ) => {
    if (bankFeedAccounts.length === 0) {
      return [];
    }

    let elements = bankFeedAccounts.map((bfAccount: any) => {
      return (
        <HoverBackground
          onMouseEnter={() => {
            setHighlightedBfAccount(bfAccount);
          }}
          onMouseLeave={() => {
            setHighlightedBfAccount(null);
          }}
          key={bfAccount.id}
          onClick={() => {
            if (linked) {
              return;
            }
            const linkingData = accountLinkingData[bfAccount.id];
            if (linkingData) {
              const updatedData = {
                ...linkingData,
                isChecked: !linkingData.isChecked,
              };
              trackStepOneAmplitudeEvent("Toggling linked bank feed account.", {
                linkingData: updatedData,
              });
              setAccountLinkingData({
                ...accountLinkingData,
                [bfAccount.id]: updatedData,
              });
            }
          }}
          className="py-4 mt-2 flex items-center border-gray-300"
          style={{
            flex: "1 0 100%",
            border: "1px solid rgba(42, 47, 69, .17)",
            borderRadius: "15px",
            height: "60px",
            overflow: "visible",
          }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%", // full width container
              padding: "10px",
            }}>
            <Checkbox
              id={bfAccount.id}
              checked={linked || accountLinkingData[bfAccount.id]?.isChecked}
              className="ml-3"
              disabled={linked || !accountLinkingData[bfAccount.id]}></Checkbox>
            <div>
              <div
                className="ml-3"
                style={{
                  height: "30px",
                  maxWidth: "190px",
                  display: "flex",
                  alignItems: "start",
                  flexDirection: "column",
                  justifyContent: bfAccount.current_balance
                    ? "space-between"
                    : "center",
                }}>
                <GenericBankFeedsAccountName
                  id={bfAccount.id}
                  text={bfAccount.name}
                  width={"190px"}
                />
                {bfAccount.current_balance && (
                  <div
                    style={{
                      height: "11px",
                      fontSize: 11,
                    }}>
                    ${bfAccount.current_balance ?? "0.00"}
                  </div>
                )}
              </div>
            </div>
            {getDropdownComponent(linked, bfAccount)}
          </div>
        </HoverBackground>
      );
    });

    // If we have fewer than INITIAL_ACCOUNT_LENGTH accounts, need to make sure the spacing looks the same, so we
    // add fake (invisible) accounts to keep spacing consistent.
    let fakeAccountsToAdd = 0;
    if (!linked && linkedBankFeedAccounts.length === 0) {
      fakeAccountsToAdd = INITIAL_ACCOUNT_LENGTH - bankFeedAccounts.length;
    } else if (
      linked &&
      unlinkedBankFeedAccounts.length + bankFeedAccounts.length <
        INITIAL_ACCOUNT_LENGTH
    ) {
      fakeAccountsToAdd =
        INITIAL_ACCOUNT_LENGTH -
        (unlinkedBankFeedAccounts.length + bankFeedAccounts.length);
    }

    for (let i = 0; i < fakeAccountsToAdd; i++) {
      elements.push(
        <div
          style={{
            flex: "1 0 100%",
            borderRadius: "15px",
            height: "60px",
          }}></div>,
      );
    }

    if (linkedBankFeedAccounts.length > 0) {
      elements = [
        <div
          className={linked ? "text-md font-bold pt-4" : "text-md font-bold"}
          key={linked ? "linked-account-label" : "unlinked-account-label"}>
          {linked ? "Linked accounts" : "Unlinked accounts"}
        </div>,
        ...elements,
      ];
    }

    return elements;
  };

  const linkAccountsHandler = () => {
    const checkedAccountLinkingData = getCheckedAccountLinkingData();
    trackStepOneAmplitudeEvent("Selected accounts to link.", {
      accountLinkingData: checkedAccountLinkingData,
    });
    setConfirmedBankFeedAccountLinkingData(checkedAccountLinkingData);
    setStep(2);
  };

  const createGLAccountHandler = (
    props: GLAccountProps,
    successHandler: () => void,
    completeHandler: () => void,
  ) => {
    const requestBody = {
      account: props,
    };

    trackStepOneAmplitudeEvent("Creating new G/L account.", {
      requestBody,
    });

    axios
      .post(`${getBackendUrl()}/versioned/accounting/accounts`, requestBody, {
        headers: {
          "X-Rutter-Lta": token,
          "X-Rutter-Version": "2024-08-31",
          "X-Rutter-Force-Sync": isDev(),
        },
      })
      .then((response) => {
        trackStepOneAmplitudeEvent("Successfully created new G/L account.", {
          requestBody,
        });
        const newAccount = response.data.account;
        setUnlinkedAccounts([newAccount, ...unlinkedAccounts]);
        linkGLAndBFAccounts(newAccount, visibleBfAccount ?? undefined);
        // Call successHandler only after EVERYTHING else has succeeded.
        successHandler();
      })
      .catch((err) => {
        trackStepOneAmplitudeEvent("Error creating new G/L account.");
        // In this case nothing about the state has changed, there was just a problem creating the account. The user can close
        // the modal and retry, but we don't need to reload, so overload the original retry callback (and set it back to the
        // default afterward).
        setRetryCallback(() => {
          return () => {
            setDefaultRetryCallback();
          };
        });

        updateErrorMessage({ error: err });
      })
      .finally(completeHandler);
  };

  return isLoading ? (
    <SecureConnection />
  ) : (
    <>
      <div className="pt-4 pr-12 pl-12">
        <div className="text-sm" style={{ textAlign: "center", fontSize: 10 }}>
          STEP ONE
        </div>
        <div className="text-lg font-bold pb-2" style={{ textAlign: "center" }}>
          {linkedBankFeedAccounts.length > 0
            ? "Review and manage your accounts"
            : "Which accounts do you want to connect?"}
        </div>
        <div className="text-sm" style={{ textAlign: "center" }}>
          {linkedBankFeedAccounts.length > 0
            ? "View which bank accounts are already linked and connect any that are missing."
            : `We'll use these to sync your bank activity. 
          ${platformName} will gain access to the following data.`}
        </div>
      </div>

      <ScrollableComponent
        minHeight={275}
        maxHeight={275}
        classStr="mt-2 ml-2 mr-3"
        // Don't allow scroll when a dropdown menu is open.
        scrollable={visibleBfAccount === null}>
        {getFormattedBankFeedAccounts(unlinkedBankFeedAccounts, false)}
        {getFormattedBankFeedAccounts(linkedBankFeedAccounts, true)}
      </ScrollableComponent>

      <CreateGLAccountModal
        store={store}
        visible={isModalVisible}
        currencies={availableCurrencies}
        subsidaries={availableSubsidiaries}
        accountType={visibleBfAccount?.bank_account_type ?? null}
        closeModal={() => {
          trackStepOneAmplitudeEvent("Cancelled G/L account creation.", {
            bfAccount: visibleBfAccount,
          });
          setVisibleBfAccount(null);
          setIsModalVisible(false);
        }}
        createGLAccountHandler={createGLAccountHandler}></CreateGLAccountModal>

      <GenericBankFeedsButton
        text={linkAccountText}
        onClick={linkAccountsHandler}
        disabled={isLoading || getCheckedAccountLinkingData().length === 0}
      />

      <div
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 9999,
        }}>
        <Toast
          className="shadow-lg rounded-3"
          onClose={() => setIsToastVisible(false)}
          show={isToastVisible}
          autohide
          delay={5000}>
          <Toast.Body>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                maxWidth: "400px",
                height: "60px",
                backgroundColor: "#fff",
              }}>
              <FiAlertCircle
                className="ml-2"
                color="red"
                size={32}
                style={{
                  flexShrink: 0,
                  marginRight: "10px",
                }}
              />
              <div>
                <div style={{ fontWeight: "bold" }}>
                  Only {totalSucceededAccounts} of your {totalAttemptedAccounts}{" "}
                  accounts were linked.
                </div>
                <div>You can retry now.</div>
              </div>
              <div
                className="ml-2 mr-2"
                style={{
                  position: "relative",
                  width: "25px",
                  height: "40px",
                }}>
                <FiX
                  onClick={() => setIsToastVisible(false)}
                  size={20}
                  style={{
                    cursor: "pointer",
                    position: "absolute",
                    top: 0,
                    right: 0,
                  }}
                />
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </div>
    </>
  );
};

const HoverBackground = styled.div`
  cursor: pointer;
  overflow: hidden;
  &:hover {
    background-color: #f9fafc;
  }
`;
